import { useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useUnitStore, type RoleKey } from '@/stores/unitStore'
import { getAccessFlags, hasAnyRole } from '@/lib/access'

type RequireRolesProps = {
  allow: RoleKey[]
  fallbackTo?: string
  children: React.ReactNode
}

export function RequireRoles({ allow, fallbackTo = '/app/consumptions', children }: RequireRolesProps) {
  const location = useLocation()
  const selectedUnitId = useUnitStore((s) => s.selectedUnitId)
  const rolesByUnit = useUnitStore((s) => s.rolesByUnit)
  const loadRoles = useUnitStore((s) => s.loadRoles)

  const units = useUnitStore((s) => s.units)
  const unitsLoaded = useUnitStore((s) => s.unitsLoaded)

  useEffect(() => {
    if (selectedUnitId) {
      void loadRoles(selectedUnitId)
      return
    }
    if (!unitsLoaded) return
    for (const u of units) void loadRoles(u.id).catch(() => undefined)
  }, [selectedUnitId, loadRoles, unitsLoaded, units])

  // "Todas as unidades": vale o papel em qualquer unidade (as telas que exigem
  // uma unidade só mostram "Selecione uma unidade" pelo RequireUnit). Antes
  // redirecionava para /app, que voltava para cá — tela em branco.
  let roles: string[] | undefined
  if (selectedUnitId) {
    roles = rolesByUnit[selectedUnitId]
  } else if (unitsLoaded && units.every((u) => rolesByUnit[u.id])) {
    roles = [...new Set(units.flatMap((u) => rolesByUnit[u.id] ?? []))]
  }
  if (!roles) {
    return (
      <div className="min-h-[30vh] grid place-items-center text-sm text-zinc-500">
        Carregando permissões...
      </div>
    )
  }

  if (!hasAnyRole(roles, allow)) {
    // Atendente de expedição não tem acesso ao fallback padrão (/app/consumptions);
    // manda pra home dele pra evitar loop de redirect.
    const access = getAccessFlags(roles)
    const target = access.isExpedicaoOnly
      ? '/app/expedicao'
      : access.isEmployeeOnly && fallbackTo === '/app/consumptions'
        ? '/app/mobile'
        : fallbackTo
    const next = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`${target}?next=${next}`} replace />
  }

  return <>{children}</>
}
